import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  BlockerRow,
  StopCause,
  MachineSummaryRow,
} from "../../services/ShiftAnalysisService";
import { formatDuration, buildMachineColorMap } from "./utils";
import { useStaggerIn } from "./anim";

interface Props {
  blockers: Map<string, BlockerRow[]>;
  stopCauses: StopCause[];
  machines: MachineSummaryRow[];
  focusedSn?: string | null;
  onHover?: (sn: string | null) => void;
  onPin?: (sn: string) => void;
}

const BlameFlow: React.FC<Props> = ({
  blockers,
  stopCauses,
  machines,
  focusedSn,
  onHover,
  onPin,
}) => {
  const { t } = useTranslation();
  const colorMap = useMemo(() => buildMachineColorMap(machines), [machines]);

  // Worst fault per machine (stopCauses is globally worst-first).
  const topFaultBySn = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of stopCauses)
      if (!m.has(c.machine_sn)) m.set(c.machine_sn, c.fault_text);
    return m;
  }, [stopCauses]);

  // One entry per victim: its dominant blocker (row [0]), sorted by blocked time.
  const items = useMemo(() => {
    const out: { victim: BlockerRow; total: number }[] = [];
    for (const rows of blockers.values()) {
      if (rows.length === 0) continue;
      const total = rows.reduce((s, r) => s + r.blocked_seconds, 0);
      out.push({ victim: rows[0], total });
    }
    return out
      .sort((a, b) => b.victim.blocked_seconds - a.victim.blocked_seconds)
      .slice(0, 6);
  }, [blockers]);

  const ref = useStaggerIn<HTMLDivElement>(".sa-blame-row", items.length, {
    y: 10,
    stagger: 55,
  });

  if (items.length === 0) return null;

  return (
    <div
      className="rounded-2xl border p-4 sm:p-5"
      style={{
        backgroundColor: "var(--bg-primary)",
        borderColor: "var(--border-primary)",
      }}
    >
      <h4
        className="text-sm font-semibold mb-1"
        style={{ color: "var(--text-primary)" }}
      >
        {t("whoBlockedWhom", "Who blocked whom")}
      </h4>
      <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
        {t(
          "blameHint",
          "When a machine waits, a neighbour usually caused it. These are the dominant blockers — fix the blocker, not the victim.",
        )}
      </p>

      <div ref={ref} className="flex flex-col gap-2">
        {items.map(({ victim }) => {
          const blockerColor =
            colorMap.get(victim.blocker_machine_sn) ?? "#94a3b8";
          const victimColor =
            colorMap.get(victim.blocked_machine_sn) ?? "#94a3b8";
          const blockerFault = topFaultBySn.get(victim.blocker_machine_sn);
          const dimmed =
            focusedSn != null &&
            focusedSn !== victim.blocker_machine_sn &&
            focusedSn !== victim.blocked_machine_sn;
          return (
            <button
              key={`${victim.blocked_machine_sn}-${victim.blocker_machine_sn}`}
              type="button"
              onClick={() => onPin?.(victim.blocker_machine_sn)}
              onMouseEnter={() => onHover?.(victim.blocker_machine_sn)}
              onMouseLeave={() => onHover?.(null)}
              className="sa-blame-row w-full text-left rounded-xl p-3 transition-all hover:brightness-95"
              style={{
                backgroundColor: "var(--bg-secondary)",
                opacity: dimmed ? 0.5 : 1,
              }}
            >
              <div className="flex items-center gap-2 flex-wrap">
                {/* victim */}
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                  style={{
                    backgroundColor: `${victimColor}22`,
                    color: victimColor,
                  }}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ backgroundColor: victimColor }}
                  />
                  {victim.blocked_machine_name}
                </span>

                {/* arrow */}
                <span
                  className="text-xs"
                  style={{ color: "var(--text-secondary)" }}
                >
                  ⟵ {t("heldUpBy", "held up by")}
                </span>

                {/* blocker */}
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold"
                  style={{ backgroundColor: blockerColor, color: "#fff" }}
                >
                  {victim.blocker_machine_name}
                </span>

                <span
                  className="ml-auto text-sm font-bold tabular-nums"
                  style={{ color: "#f59e0b" }}
                >
                  {formatDuration(victim.blocked_seconds)}
                </span>
              </div>

              <div
                className="mt-1.5 text-xs"
                style={{ color: "var(--text-secondary)" }}
              >
                {t("acrossEpisodes", "across {{count}} episodes", {
                  count: victim.episodes,
                })}
                {blockerFault && (
                  <>
                    {" · "}
                    {t("blockerFault", "blocker's main fault")}:{" "}
                    <span
                      className="font-medium"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {blockerFault}
                    </span>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BlameFlow;
